import { ReactElement } from 'react'
import { Box, Switch, useTheme } from '@mui/material'
import { AutosaveIndicator } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'

export type ListStatusBarProps = {
  onlyWarnings: boolean
  onOnlyWarningsChange: (next: boolean) => void
  warnCount: number
  isSaving: boolean
  dirty: boolean
  /** Flush the pending autosave immediately. */
  onSaveNow: () => void
  visibleCount: number
  totalCount: number
}

/** Status row below the toolbar: warnings filter, autosave indicator and visible/total count. */
export const ListStatusBar = ({
  onlyWarnings,
  onOnlyWarningsChange,
  warnCount,
  isSaving,
  dirty,
  onSaveNow,
  visibleCount,
  totalCount,
}: ListStatusBarProps): ReactElement => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: 11,
        color: theme.palette.text.secondary,
        background: onlyWarnings ? 'rgba(237,108,2,0.06)' : 'transparent',
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Switch
          size="small"
          checked={onlyWarnings}
          onChange={e => onOnlyWarningsChange(e.target.checked)}
        />
        <Box component="span" sx={{ fontFamily: 'Roboto', fontSize: 12 }}>
          Nur Warnungen
        </Box>
      </Box>
      {warnCount > 0 ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontFamily: 'Roboto',
            fontSize: 11,
            lineHeight: '13px',
            color: theme.palette.warning.dark,
          }}
        >
          <MaterialSymbol icon="warning" size={13} />
          {warnCount}
        </Box>
      ) : null}
      <Box
        sx={{
          ml: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: 11,
        }}
      >
        <AutosaveIndicator isSaving={isSaving} dirty={dirty} onSaveNow={onSaveNow} />
        {visibleCount} / {totalCount}
      </Box>
    </Box>
  )
}
